import { TwoUIProgressView, TwoUIProgressViewOperationData, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { OmitStrict } from "@amx/ux-twoui-utils";
import { ActionButton as FluentActionButton, HighContrastSelector, IButtonProps, IPalette, PrimaryButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React, { useEffect, useRef } from "react";
import { useComponentLogger } from "../../../shared/logging";
import { validateProps } from "../../../shared/props";
import { getColorFromCorrectPalette } from "../../../styles/colors";
import { TextAlignment } from "../../../styles/fonts";
import { NoHrefLink } from "../..//Links/NoHrefLink/NoHrefLink";
import { ErrorCallout, ErrorCalloutProps } from "../../ErrorCallout/ErrorCallout";
import { Icon } from "../../Icon/Icon";
import { Label } from "../../Label/Label";
import { NoHrefLinkProps } from "../../Links/NoHrefLink/NoHrefLink";
import { TooltipHost, TooltipHostProps } from "../../TooltipHost/TooltipHost";
import { ButtonIconNames, LinkButtonProps, OnClickButtonProps, TextButtonProps } from "../shared/props";
import { computeActionButtonHighContrastColor, computeActionButtonIconColor, computeActionButtonTextColor} from "./ActionButton.styles";

export type ActionButtonErrorIcon = "Error";

export interface ComponentProps {
    // ... original props data
    readonly operationData?: ComponentOperationData;
}

export interface ComponentOperationData {
    readonly progressViewOperationData: TwoUIProgressViewOperationData;
    // and stuff that is needed for operation
}

export type BaseActionButtonProps = (TextButtonProps & (OnClickButtonProps | LinkButtonProps) & {
    /**
     * Information about the monitored operation.
     */
    readonly operationData?: ActionButtonOperationData;

    /**
     * Name of icon to display
     */
    readonly iconName?: ButtonIconNames;

    /**
     * Props for Tooltip
     */
    readonly tooltipHostProps?: TooltipHostProps;

    /**
     * Alignment of the text within the button; defaults to "center"
     */
    readonly textAlignment?: TextAlignment;
});

export type ActionButtonColor = Extract<keyof IPalette, "neutralPrimary" | "themePrimary">;

export type PrimaryActionButtonProps = BaseActionButtonProps & {
    readonly type: "primary";
};

export type ErrorActionButtonProps = BaseActionButtonProps & {
    readonly type: "error";
};

export type DefaultActionButtonProps = BaseActionButtonProps & {
    readonly type?: "default";
    /**
     * Color of both icon and text, if not set it defaults to themePrimary
     */
    readonly buttonColor?: ActionButtonColor;
};

export type ActionButtonProps = PrimaryActionButtonProps | DefaultActionButtonProps | ErrorActionButtonProps;

export type ActionButtonOperationData = BaseActionButtonOperationData & (RetryButtonActionButtonOperationData | ErrorCalloutActionButtonOperationData);

interface BaseActionButtonOperationData {
    /**
     * Contain information of the operation dropdown with attach it self to
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;

    /**
     * Text which will show when in the updating state.
     */
    readonly textWhileUpdating?: string;
}

interface RetryButtonActionButtonOperationData {
    /**
     * Props for error action when an operation failed.
     */
    readonly retryActionButton?: RetryActionButtonProps;
}

interface ErrorCalloutActionButtonOperationData {
    /**
     * Callout to show upon operation error
     */
    readonly errorCallout?: OmitStrict<ErrorCalloutProps, "targetOrTriggerButton">;
}

export interface RetryActionButtonProps {

    /**
     * Name of glyph in error state. If not provided then default to "Info".
     */
    readonly errorIconName?: ActionButtonErrorIcon;

    /**
     * Error message when operation failed.
     */
    readonly errorMessage: string;

    /**
     * Error message aria label when operation failed.
     */
    readonly errorMessageAriaLabel: string;

    /**
     * The button to retry operation
     */
    readonly retryLink: NoHrefLinkProps;
}

export const RetryActionButton: React.FC<RetryActionButtonProps> = ({
    errorIconName = "Error",
    errorMessage,
    errorMessageAriaLabel,
    retryLink,
    children,
    ...otherProps
}) => {
    validateProps("RetryActionButton", otherProps);

    return (
        <Stack horizontal={true} tokens={{ childrenGap: 8 }} style={{ display: "inline-flex" }}>
            <Stack tokens={{ padding: "5px 0 0 0" }}>
                <Icon iconName={errorIconName} color="errorIcon" size="12px" />
            </Stack>
            <Stack horizontal={false}>
                <Label aria-label={errorMessageAriaLabel}>{errorMessage}</Label>
                <NoHrefLink {...retryLink} />
            </Stack>
        </Stack>
    );
};

export const ActionButton: React.FC<ActionButtonProps> = (props) => {
    const {
        text,
        "aria-label": ariaLabel,
        disabled = false,
        iconName,
        textAlignment = "center",
        operationData,
        type: buttonType,
        ...otherProps
    } = props;

    useComponentLogger("ActionButton", otherProps.id);

    validateProps("ActionButton", otherProps);

    const twoUITelemetry = useTwoUITelemetry();

    const ComputedButton = buttonType === "primary" ? PrimaryButton : FluentActionButton;

    let computedButtonColors = computeActionButtonIconColor(props.type, disabled);

    if (props.type === "default" || !props.type) {
        computedButtonColors = computeActionButtonTextColor(props.type, disabled, props.buttonColor);
    }

    const theme = useTheme();
    const buttonProps: IButtonProps = {
        text,
        disabled,
        ariaLabel,
        styles: {
            // Use `getColorFromCorrectPalette` as ActionButton's `computeActionButtonColor` method must return
            // a colour token, due to it being passed to Icon too, and this token could be in either
            // `palette` or `semanticColors`
            root: {
                color: getColorFromCorrectPalette(theme, computedButtonColors.rest),
                textAlign: textAlignment,
                [HighContrastSelector]: {
                    ":focus::after": {
                        // for focus border to display well without interfere other component
                        margin: "1px"
                    },
                    color: computeActionButtonHighContrastColor(props.type)
                }
            },
            rootHovered: {
                color: getColorFromCorrectPalette(theme, computedButtonColors.hover),
            },
            rootPressed: {
                color: getColorFromCorrectPalette(theme, computedButtonColors.pressed)
            }
        },
        onRenderIcon:
            iconName ? () =>
            (
                <Icon
                    size="16px"
                    iconName={iconName}
                />
            )
                : undefined,
        ...otherProps,
    };

    const href = otherProps["href"];
    useEffect(() => {
        twoUITelemetry.checkUrlAndReportHttp(href);
    }, [href]);

    const finishedView = () => {
        if (otherProps.tooltipHostProps) {
            return (
                <TooltipHost {...otherProps.tooltipHostProps}>
                    <ComputedButton {...buttonProps} />
                </TooltipHost>
            );
        }
        return <ComputedButton {...buttonProps} />;
    };

    if (operationData) {
        let errorView = finishedView();
        if ("retryActionButton" in operationData && operationData.retryActionButton) {
            errorView = <RetryActionButton {...operationData.retryActionButton} />;
        } else if ("errorCallout" in operationData && operationData.errorCallout) {
            errorView = (
                <ErrorCalloutActionButton
                    as={ComputedButton}
                    buttonProps={buttonProps}
                    errorCalloutProps={operationData.errorCallout}
                />
            );
        }

        const loadingButtonProps: IButtonProps = {
            ...buttonProps,
            text: operationData.textWhileUpdating || text,
            disabled: true,
            onRenderIcon: () => <Spinner size={SpinnerSize.small} style={{ marginLeft: "4px", marginRight: "4px" }} />
        };

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                updatingView={(
                    <ComputedButton {...loadingButtonProps} />
                )}
                updateFailedView={errorView}
                finishedView={finishedView()}
                // If first-load fails, it is safer to show nothing ("undefined" view) than a button linked to incomplete data
                failedView={undefined}
            />
        );
    } else {
        return finishedView();
    }
};

interface ErrorCalloutActionButtonProps {
    /** The underlying element to render */
    readonly as: typeof PrimaryButton | typeof FluentActionButton;

    /** Props to be passed to the underlying button */
    readonly buttonProps: IButtonProps;

    /** Props to be passed to the underlying error callout */
    readonly errorCalloutProps: OmitStrict<ErrorCalloutProps, "targetOrTriggerButton">;
}

const ErrorCalloutActionButton: React.FC<ErrorCalloutActionButtonProps> = ({
    as: ComputedButton,
    buttonProps,
    errorCalloutProps
}) => {
    const ref = useRef<HTMLElement>(null);

    return (
        <>
            <ComputedButton elementRef={ref} {...buttonProps} />
            <ErrorCallout targetOrTriggerButton={ref} {...errorCalloutProps} />
        </>
    );
};
